import { useApolloClient, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { EntityPagesArticlesModuleArticlesArticleDeleteDocument } from '../graphql/mutations/entity-pages-articles-module-articles-article-delete.gql-types';
import { EntityPagesArticlesModuleContentArticleDeleteDocument } from '../graphql/mutations/entity-pages-articles-module-content-article-delete.gql-types';
import { EntityPagesArticleModuleListDocument } from '../graphql/queries/entity-pages-article-module-list.gql-types';
import type { ArticleCardView } from '../types/types';

import { useArticlesModuleContext } from './use-articles-module-context/use-articles-module-context';

export const useRemoveArticleMutation = (): [
  (article: ArticleCardView) => Promise<void>,
  { loading: boolean },
] => {
  const { variablesQuery, refetch } = useArticlesModuleContext();
  const apolloClient = useApolloClient();
  const { $t } = useIntl();

  const [handleContentArticleDeletion, contentArticleState] = useMutation(
    EntityPagesArticlesModuleContentArticleDeleteDocument
  );

  const [handleArticlesArticleDeletion, articlesArticleState] = useMutation(
    EntityPagesArticlesModuleArticlesArticleDeleteDocument
  );

  return [
    useCallback(
      async (article: ArticleCardView) => {
        try {
          if (article.typename === 'ContentArticle') {
            const { data, errors } = await handleContentArticleDeletion({
              variables: {
                input: { globalId: article.globalId },
              },
            });

            const error = data?.contentDeleteArticle?.error || errors?.[0];
            if (error) {
              throw Error($t({ id: 'EP_GENERAL_FEEDBACK_ERROR' }), {
                cause: error,
              });
            }
          } else {
            const { data, errors } = await handleArticlesArticleDeletion({
              variables: {
                input: { articleGlobalId: article.globalId },
              },
            });

            const error =
              data?.articlesDeleteArticle?.__typename === 'ArticlesError' ||
              errors?.[0];
            if (error) {
              throw Error($t({ id: 'EP_GENERAL_FEEDBACK_ERROR' }), {
                cause: error,
              });
            }
          }
        } catch (error) {
          throw Error($t({ id: 'EP_GENERAL_FEEDBACK_ERROR' }), {
            cause: error,
          });
        }

        const existingData = apolloClient.readQuery({
          query: EntityPagesArticleModuleListDocument,
          variables: variablesQuery,
        });

        if (
          existingData?.contentPageFeaturedArticle?.object.globalId ===
          article.globalId
        ) {
          apolloClient.cache.writeQuery({
            query: EntityPagesArticleModuleListDocument,
            variables: variablesQuery,
            data: {
              ...existingData,
              contentPageFeaturedArticle: undefined,
            },
          });
        } else {
          if (!existingData?.contentPageArticles?.edges) {
            refetch();
            return;
          }

          const newContentPageArticles = {
            ...existingData.contentPageArticles,
            edges: existingData.contentPageArticles.edges.filter(
              (edge) => edge?.node.object.globalId !== article.globalId
            ),
          };

          apolloClient.cache.writeQuery({
            query: EntityPagesArticleModuleListDocument,
            variables: variablesQuery,
            data: {
              ...existingData,
              contentPageArticles: newContentPageArticles,
            },
          });
        }

        return;
      },
      [handleContentArticleDeletion, handleArticlesArticleDeletion, $t]
    ),
    { loading: contentArticleState.loading || articlesArticleState.loading },
  ];
};
